import { NavLink, Outlet } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import apiClient from '../../lib/apiClient';
import AdminLoginPage from './AdminLoginPage';

const NAV_ITEMS = [
  { to: '/admin', label: 'Overview', icon: 'grid' },
  { to: '/admin/users', label: 'Users', icon: 'users' },
  { to: '/admin/revenue', label: 'Revenue', icon: 'dollar' },
  { to: '/admin/usage', label: 'Usage', icon: 'activity' },
  { to: '/admin/documents', label: 'Documents', icon: 'file' },
  { to: '/admin/integrations', label: 'Integrations', icon: 'link' },
  { to: '/admin/esign', label: 'E-Signatures', icon: 'pen' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings' },
] as const;

function NavIcon({ icon }: { icon: string }) {
  const s = { width: 18, height: 18, strokeWidth: 1.5, fill: 'none', stroke: 'currentColor' };
  switch (icon) {
    case 'grid': return <svg {...s} viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>;
    case 'users': return <svg {...s} viewBox="0 0 24 24"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>;
    case 'dollar': return <svg {...s} viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" /></svg>;
    case 'activity': return <svg {...s} viewBox="0 0 24 24"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>;
    case 'file': return <svg {...s} viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>;
    case 'link': return <svg {...s} viewBox="0 0 24 24"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /></svg>;
    case 'pen': return <svg {...s} viewBox="0 0 24 24"><path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" /></svg>;
    case 'settings': return <svg {...s} viewBox="0 0 24 24"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" /></svg>;
    default: return null;
  }
}

// ─── Admin token management (module-level, survives re-renders) ──

let _adminInterceptorId: number | null = null;

function installAdminInterceptor(token: string) {
  // Remove previous interceptor if any
  if (_adminInterceptorId != null) {
    apiClient.interceptors.request.eject(_adminInterceptorId);
  }
  _adminInterceptorId = apiClient.interceptors.request.use((config) => {
    if (config.url?.startsWith('/api/admin')) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
}

function removeAdminInterceptor() {
  if (_adminInterceptorId != null) {
    apiClient.interceptors.request.eject(_adminInterceptorId);
    _adminInterceptorId = null;
  }
}

// Install interceptor eagerly if token exists in localStorage on module load
const _storedToken = typeof localStorage !== 'undefined' ? localStorage.getItem('corner:admin-token') : null;
if (_storedToken) installAdminInterceptor(_storedToken);

// ─── Component ───────────────────────────────────

export default function AdminLayout() {
  const [adminToken, setAdminToken] = useState<string | null>(_storedToken);
  const [verified, setVerified] = useState(false);
  const [checking, setChecking] = useState(true);

  // Verify stored token on mount
  useEffect(() => {
    if (!adminToken) {
      setChecking(false);
      return;
    }
    axios.get('/api/admin/auth/verify', {
      headers: { Authorization: `Bearer ${adminToken}` },
    })
      .then(() => setVerified(true))
      .catch(() => {
        localStorage.removeItem('corner:admin-token');
        removeAdminInterceptor();
        setAdminToken(null);
      })
      .finally(() => setChecking(false));
  }, [adminToken]);

  const handleLogin = useCallback((token: string) => {
    installAdminInterceptor(token);
    setAdminToken(token);
    setVerified(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem('corner:admin-token');
    removeAdminInterceptor();
    setAdminToken(null);
    setVerified(false);
  }, []);

  if (checking) {
    return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', backgroundColor: '#1A1714', color: '#8B7D6B', fontFamily: '"Geist", system-ui, sans-serif' }}>Verifying...</div>;
  }

  if (!adminToken || !verified) {
    return <AdminLoginPage onLogin={handleLogin} />;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', fontFamily: '"Geist", system-ui, sans-serif', backgroundColor: '#FDFCFB' }}>
      {/* Sidebar */}
      <aside style={{ width: 220, backgroundColor: '#2C2218', display: 'flex', flexDirection: 'column', padding: '20px 12px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 8px', marginBottom: 32 }}>
          <svg width="24" height="24" viewBox="0 0 256 256" fill="none" style={{ flexShrink: 0 }}>
            <defs>
              <linearGradient id="admin-logo-grad" x1="42" y1="38.5" x2="207" y2="209" gradientUnits="userSpaceOnUse">
                <stop stopColor="#D46A3A" />
                <stop offset="1" stopColor="#8B3A10" />
              </linearGradient>
            </defs>
            <path d="M236 152V32C236 25.373 230.627 20 224 20H32C25.373 20 20 25.373 20 32L20 224C20 230.627 25.373 236 32 236H152" stroke="url(#admin-logo-grad)" strokeWidth="40" strokeLinecap="round" />
          </svg>
          <span style={{ color: '#fff', fontSize: 15, fontWeight: 600 }}>Corner Admin</span>
        </div>

        <nav style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1 }}>
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/admin'}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                borderRadius: 8,
                textDecoration: 'none',
                fontSize: 14,
                fontWeight: isActive ? 500 : 400,
                color: isActive ? '#D46A3A' : 'rgba(255,255,255,0.7)',
                backgroundColor: isActive ? 'rgba(212,106,58,0.12)' : 'transparent',
                transition: 'all 0.15s',
              })}
            >
              <NavIcon icon={item.icon} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        {/* Logout */}
        <button
          onClick={handleLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 8,
            border: 'none', backgroundColor: 'transparent', color: 'rgba(255,255,255,0.5)',
            fontSize: 13, cursor: 'pointer', fontFamily: '"Geist", system-ui, sans-serif',
          }}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><polyline points="16 17 21 12 16 7" /><line x1="21" y1="12" x2="9" y2="12" /></svg>
          Sign out
        </button>
      </aside>

      {/* Main content */}
      <main style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        <Outlet />
      </main>
    </div>
  );
}
